/**
 * LevelProgressRing — Circular SVG ring around the user avatar in navbar.
 *
 * Shows progress within the current level as a ring stroke.
 * Includes a small level badge at bottom-right.
 * Tooltip shows the level name.
 */

import React from 'react';
import { Box, Tooltip, Typography, useTheme, type Theme } from '@mui/material';
import { LEVEL_CONFIG, CERTIFICATION_TITLE } from '../../data/levelUpConfig';
import { typography } from '../../common/styles/universalTheme';

interface LevelProgressRingProps {
  level: number;
  progressPercent: number; // 0-100
  children: React.ReactNode;
}

const RING_SIZE = 44; // matches medium avatar
const STROKE_WIDTH = 2.5;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function getRingColor(level: number, theme: Theme): string {
  if (level >= 4) return theme.palette.success.main;
  if (level >= 2) return theme.palette.text.primary;
  return theme.palette.text.disabled;
}

export default function LevelProgressRing({ level, progressPercent, children }: LevelProgressRingProps) {
  const theme = useTheme();
  const levelInfo = LEVEL_CONFIG[level] ?? LEVEL_CONFIG[0];
  const ringColor = getRingColor(level, theme);
  const offset = CIRCUMFERENCE - (Math.min(progressPercent, 100) / 100) * CIRCUMFERENCE;

  const tooltipText = `${CERTIFICATION_TITLE} — ${levelInfo.name}`;

  return (
    <Tooltip title={tooltipText} arrow>
      <Box sx={{ position: 'relative', width: RING_SIZE, height: RING_SIZE }}>
        {/* SVG ring */}
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}
        >
          {/* Background ring */}
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth={STROKE_WIDTH}
          />
          {/* Progress ring */}
          {level > 0 && (
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={ringColor}
              strokeWidth={STROKE_WIDTH}
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.4s ease' }}
            />
          )}
        </svg>

        {/* Avatar (centered inside ring) */}
        <Box
          sx={{
            position: 'absolute',
            top: STROKE_WIDTH,
            left: STROKE_WIDTH,
            width: RING_SIZE - STROKE_WIDTH * 2,
            height: RING_SIZE - STROKE_WIDTH * 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>

        {/* Level badge */}
        {level > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -3,
              right: -6,
              height: 14,
              px: 0.5,
              borderRadius: '7px',
              bgcolor: ringColor,
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1.5px solid ${theme.palette.background.paper}`,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography sx={{ fontSize: 8, fontWeight: typography.fontWeight.bold, lineHeight: 1, letterSpacing: 0.2 }}>
              Lv{level}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}
